import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { MessageSquareText, X, Send, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MARKETING_ROUTES = ["/", "/plumbers", "/electricians", "/roofers", "/contractors", "/compare", "/resources"];

const CHIPS = [
  "How much does it cost?",
  "Does it work with ServiceTitan?",
  "What happens in an emergency?",
  "How fast is setup?",
];

const FAIL_MSG = "Something hiccuped — try again, or leave a demo request on the form and we'll answer everything on a call.";

export default function ChatWidget() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [summaryEmail, setSummaryEmail] = useState("");
  const [summaryState, setSummaryState] = useState(null); // null | "sending" | "sent" | "error"
  const scrollRef = useRef(null);
  const sessionRef = useRef(
    sessionStorage.getItem("bf_chat_sid") ||
      (() => {
        const s = crypto.randomUUID();
        sessionStorage.setItem("bf_chat_sid", s);
        return s;
      })()
  );

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open]);

  if (!MARKETING_ROUTES.includes(pathname)) return null;

  const sendSummary = async () => {
    const email = summaryEmail.trim();
    if (!email || summaryState === "sending") return;
    setSummaryState("sending");
    try {
      const resp = await fetch(`${API}/chat/summary`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionRef.current, email }),
      });
      if (!resp.ok) throw new Error();
      setSummaryState("sent");
    } catch {
      setSummaryState("error");
    }
  };

  const setLastAssistant = (text, append = false) =>
    setMessages((m) => {
      const next = [...m];
      const last = next[next.length - 1];
      next[next.length - 1] = { role: "assistant", text: append ? last.text + text : text };
      return next;
    });

  const send = async (preset) => {
    const msg = (preset ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", text: msg }, { role: "assistant", text: "" }]);
    try {
      const resp = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionRef.current, message: msg }),
      });
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        setLastAssistant(typeof data.detail === "string" ? data.detail : FAIL_MSG);
        setBusy(false);
        return;
      }
      const reader = resp.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const part of parts) {
          if (!part.startsWith("data: ")) continue;
          try {
            const payload = JSON.parse(part.slice(6));
            if (payload.t) setLastAssistant(payload.t, true);
            if (payload.error) setLastAssistant(payload.error);
          } catch {}
        }
      }
    } catch {
      setLastAssistant(FAIL_MSG);
    }
    setBusy(false);
  };

  return (
    <>
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="chat-widget-button"
        aria-label={open ? "Close chat" : "Ask a question"}
        className="fixed bottom-5 right-5 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-[#F25F22] text-white shadow-[0_8px_30px_rgba(242,95,34,0.45)] transition-all duration-200 hover:scale-105 hover:bg-[#FF7539]"
      >
        {open ? <X className="h-6 w-6" /> : <MessageSquareText className="h-6 w-6" />}
      </button>

      {open && (
        <div
          data-testid="chat-panel"
          className="fixed bottom-[84px] right-5 z-50 flex h-[520px] w-[calc(100vw-40px)] max-w-sm flex-col overflow-hidden rounded-xl border border-white/[0.1] bg-[#12161F] shadow-2xl"
        >
          <div className="border-b border-white/[0.08] bg-[#0B0D10] px-5 py-4">
            <p className="font-display text-sm font-bold text-slate-50">Ask Backfill</p>
            <p className="mt-0.5 flex items-center gap-1.5 text-xs text-slate-500">
              <span className="relative flex h-1.5 w-1.5">
                <span className="ping-slow absolute inline-flex h-full w-full rounded-full bg-emerald-400" />
                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-400" />
              </span>
              Answers in seconds — pricing, setup, CRMs, emergencies
            </p>
          </div>

          <div ref={scrollRef} data-testid="chat-messages" className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
            {messages.length === 0 && (
              <div className="space-y-3 pt-2">
                <p className="rounded-xl border border-white/[0.08] bg-[#1A212E] px-3.5 py-2.5 text-[13px] leading-relaxed text-slate-200">
                  Hey — I can answer anything about Backfill: what it does, pricing, setup, CRMs. What
                  are you wondering?
                </p>
                <div className="flex flex-wrap gap-2">
                  {CHIPS.map((c, i) => (
                    <button
                      key={c}
                      onClick={() => send(c)}
                      data-testid={`chat-chip-${i}`}
                      className="rounded-full border border-[#F25F22]/40 px-3 py-1.5 text-xs font-medium text-[#F25F22] transition-colors hover:bg-[#F25F22]/10"
                    >
                      {c}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  data-testid={`chat-message-${i}`}
                  className={`max-w-[85%] rounded-xl px-3.5 py-2.5 text-[13px] leading-relaxed ${
                    m.role === "user"
                      ? "bg-[#F25F22] text-white"
                      : "border border-white/[0.08] bg-[#1A212E] text-slate-200"
                  }`}
                >
                  {m.text || (busy && m.role === "assistant" && i === messages.length - 1 ? (
                    <Loader2 className="h-4 w-4 animate-spin text-slate-500" />
                  ) : null)}
                </div>
              </div>
            ))}
          </div>

          <div className="border-t border-white/[0.08] bg-[#0B0D10] p-3">
            {messages.some((m) => m.role === "assistant") && !summaryOpen && summaryState !== "sent" && (
              <button
                onClick={() => setSummaryOpen(true)}
                data-testid="chat-summary-button"
                className="mb-2.5 w-full text-center font-mono text-[10px] uppercase tracking-widest text-slate-500 transition-colors hover:text-[#F25F22]"
              >
                Email me this chat
              </button>
            )}
            {summaryOpen && summaryState !== "sent" && (
              <div className="mb-2.5 flex items-center gap-2">
                <input
                  type="email"
                  value={summaryEmail}
                  onChange={(e) => setSummaryEmail(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && sendSummary()}
                  data-testid="chat-summary-email-input"
                  placeholder="you@yourcompany.com"
                  className="w-full min-w-0 rounded-md border border-white/[0.1] bg-[#12161F] px-3 py-2 text-xs text-slate-100 placeholder:text-slate-600 outline-none focus:border-[#F25F22]/60"
                />
                <button
                  onClick={sendSummary}
                  disabled={summaryState === "sending" || !summaryEmail.trim()}
                  data-testid="chat-summary-send"
                  className="shrink-0 rounded-md bg-[#F25F22] px-3 py-2 text-xs font-semibold text-white hover:bg-[#FF7539] disabled:opacity-50"
                >
                  {summaryState === "sending" ? "…" : "Send"}
                </button>
              </div>
            )}
            {summaryState === "sent" && (
              <p data-testid="chat-summary-sent" className="mb-2.5 text-center text-xs text-emerald-400">
                On its way — check your inbox.
              </p>
            )}
            {summaryState === "error" && (
              <p data-testid="chat-summary-error" className="mb-2.5 text-center text-xs text-red-400">
                Couldn&apos;t send — try again.
              </p>
            )}
            <div className="flex items-center gap-2">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                data-testid="chat-input"
                placeholder="Type your question…"
                maxLength={800}
                className="w-full min-w-0 rounded-md border border-white/[0.1] bg-[#12161F] px-3.5 py-2.5 text-sm text-slate-100 placeholder:text-slate-600 outline-none focus:border-[#F25F22]/60"
              />
              <button
                onClick={() => send()}
                disabled={busy || !input.trim()}
                data-testid="chat-send-button"
                className="flex shrink-0 items-center justify-center rounded-md bg-[#F25F22] p-2.5 text-white transition-colors hover:bg-[#FF7539] disabled:opacity-50"
              >
                <Send className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
